import { useCallback, useEffect, useMemo, useState } from "react";
import AlertComponent from "../../../helpers/alert/AlertComponent";
import {
  getAlertedProductsParameterCatalog,
  getAlertedProductsSolicitudes,
} from "../../../entities/alerted-product";
import {
  ALERT_CATEGORY_PARAMETER_TYPE_ID, ALERT_MANAGEMENT_PARAMETER_TYPE_ID,
  SOLICITUD_TYPE_PARAMETER_TYPE_ID,
  buildManagementPillMap, buildPillMap, defaultFilters,
} from "./alertManagementConstants";

export const useAlertManagementFilters = () => {
  const [alertCategoryOptions, setAlertCategoryOptions] = useState([]);
  const [alertManagementOptions, setAlertManagementOptions] = useState([]);
  const [solicitudTypeOptions, setSolicitudTypeOptions] = useState([]);
  const [loadingAlertCategory, setLoadingAlertCategory] = useState(false);
  const [loadingAlertManagement, setLoadingAlertManagement] = useState(false);
  const [loadingSolicitudType, setLoadingSolicitudType] = useState(false);
  const [draftFilters, setDraftFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const loadAlertCategories = useCallback(async () => {
    setLoadingAlertCategory(true);
    try {
      const opts = await getAlertedProductsParameterCatalog(ALERT_CATEGORY_PARAMETER_TYPE_ID);
      setAlertCategoryOptions(opts);
    } catch { setAlertCategoryOptions([]); } finally { setLoadingAlertCategory(false); }
  }, []);

  const loadAlertManagements = useCallback(async () => {
    setLoadingAlertManagement(true);
    try {
      const opts = await getAlertedProductsParameterCatalog(ALERT_MANAGEMENT_PARAMETER_TYPE_ID);
      setAlertManagementOptions(opts);
    } catch { setAlertManagementOptions([]); } finally { setLoadingAlertManagement(false); }
  }, []);

  const loadSolicitudTypes = useCallback(async () => {
    setLoadingSolicitudType(true);
    try {
      const opts = await getAlertedProductsParameterCatalog(SOLICITUD_TYPE_PARAMETER_TYPE_ID);
      setSolicitudTypeOptions(opts);
    } catch { setSolicitudTypeOptions([]); } finally { setLoadingSolicitudType(false); }
  }, []);

  useEffect(() => {
    loadAlertCategories();
    loadAlertManagements();
    loadSolicitudTypes();
  }, [loadAlertCategories, loadAlertManagements, loadSolicitudTypes]);

  useEffect(() => {
    if (!appliedFilters) return;
    const fetch = async () => {
      setTableLoading(true);
      // Arrancar en [] para no mezclar resultados de la consulta anterior.
      setDataSource([]);
      setTotalRecords(0);
      try {
        const result = await getAlertedProductsSolicitudes({
          ...appliedFilters,
          page: currentPage,
          pageSize,
        });
        setDataSource(result.rows);
        setTotalRecords(result.meta?.total_registros ?? 0);
      } catch {
        setDataSource([]);
        setTotalRecords(0);
        AlertComponent.error("Error", "No fue posible cargar las gestiones de alertas.");
      } finally { setTableLoading(false); }
    };
    fetch();
  }, [appliedFilters, currentPage, pageSize, refreshKey]);

  const alertCategoryPillMap   = useMemo(() => buildPillMap(alertCategoryOptions), [alertCategoryOptions]);
  const alertManagementPillMap = useMemo(() => buildManagementPillMap(alertManagementOptions), [alertManagementOptions]);

  const updateDraft    = (key) => (val) => setDraftFilters((prev) => ({ ...prev, [key]: val }));
  const handleSearch   = () => { setAppliedFilters({ ...draftFilters }); setCurrentPage(1); };
  const handleClear    = () => {
    setDraftFilters(defaultFilters);
    setAppliedFilters(null);
    setDataSource([]);
    setTotalRecords(0);
    setCurrentPage(1);
  };
  const refreshTable   = useCallback(() => setRefreshKey((k) => k + 1), []);
  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  return {
    alertCategoryOptions, alertManagementOptions, solicitudTypeOptions,
    loadingAlertCategory, loadingAlertManagement, loadingSolicitudType,
    draftFilters, appliedFilters, dataSource, tableLoading,
    alertCategoryPillMap, alertManagementPillMap,
    currentPage, pageSize, totalRecords,
    updateDraft, handleSearch, handleClear, refreshTable, handlePageChange,
  };
};